/**
 * Phase COI-REFRESH (Part B) — dashboard widget listing rentals whose
 * COI expires soon (default 60-day window).
 *
 * Columns: Customer · Rental (equipment + unit) · Expires · Days remaining
 * · Refresh state · Days since refresh sent.
 * Row click → navigate to admin rental detail.
 *
 * Bonus per phase spec: "Days since refresh sent" is derived from
 * ``latest_refresh.created_at`` on the individual refresh-status
 * endpoint. To avoid N sequential HTTP calls, we hydrate the value
 * lazily on hover / on-mount for the top rows only. When we haven't
 * fetched it yet (or when null on the server), the cell renders ``—``
 * (NOT "0 days ago").
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import api from "@/lib/api";
import { ShieldAlert, ArrowUpRight } from "lucide-react";

function fmtDate(iso) {
    if (!iso) return "—";
    return iso.slice(0, 10);
}

// Days between now and a server-supplied ISO timestamp. Returns null
// when input is missing so callers can render the "—" placeholder.
function daysSince(iso) {
    if (!iso) return null;
    try {
        const then = new Date(iso).getTime();
        if (Number.isNaN(then)) return null;
        const diff = Date.now() - then;
        return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
    } catch { return null; }
}

const REFRESH_STATE_LABEL = {
    not_sent_yet: "Not sent",
    awaiting_response: "Awaiting response",
    refresh_received: "Received",
};
const REFRESH_STATE_CHIP = {
    not_sent_yet: "bg-slate-100 text-slate-700 ring-slate-200",
    awaiting_response: "bg-amber-50 text-amber-900 ring-amber-200",
    refresh_received: "bg-emerald-50 text-emerald-900 ring-emerald-200",
};

function DaysUntilChip({ days }) {
    let cls = "bg-slate-100 text-slate-700 ring-slate-200";
    if (days < 0) cls = "bg-red-50 text-red-700 ring-red-200";
    else if (days < 7) cls = "bg-red-50 text-red-700 ring-red-200";
    else if (days < 30) cls = "bg-amber-50 text-amber-900 ring-amber-200";
    else if (days <= 90) cls = "bg-emerald-50 text-emerald-800 ring-emerald-200";
    return (
        <span
            className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset " + cls}
            data-testid="coi-widget-days-chip"
        >
            {days < 0 ? `expired ${Math.abs(days)}d ago` : `${days}d`}
        </span>
    );
}

export function CoiExpiringSoonCard() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    // Cache of rentalId → daysSinceRefreshSent (null when nothing
    // fired yet). Hydrated on mount for the top N rows.
    const [refreshDays, setRefreshDays] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/coi/expiring-soon", { params: { days: 60 } });
                const rows = data?.items || [];
                if (!cancelled) setItems(rows);
                // Hydrate refresh timestamps for the first 10 rows only —
                // enough to fill the visible area without blowing the
                // dashboard load budget.
                const top = rows.slice(0, 10);
                await Promise.all(top.map(async (r) => {
                    try {
                        const { data: st } = await api.get(`/admin/coi/refresh-status/${r.rental_id}`);
                        const iso = st?.latest_refresh?.created_at || null;
                        if (!cancelled) {
                            setRefreshDays((prev) => ({
                                ...prev,
                                [r.rental_id]: daysSince(iso),
                            }));
                        }
                    } catch {
                        // Silent — the "—" placeholder is the correct
                        // rendering when we can't hydrate.
                    }
                }));
            } catch {
                // Silent — the outer DashboardCardShell handles empty state.
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const rows = items;

    return (
        <Card
            data-testid="admin-card-coi-expiring-soon-widget"
            className="h-full col-span-1 md:col-span-2 lg:col-span-3"
        >
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                <div className="space-y-1.5">
                    <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                        <ShieldAlert size={16} className="text-blue-900" />
                        COI expiring soon
                    </CardTitle>
                    <CardDescription className="text-sm text-slate-500">
                        Rentals whose COI expires in the next 60 days. Row click opens the rental.
                    </CardDescription>
                </div>
                <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset bg-slate-100 text-slate-700 ring-slate-200"
                    data-testid="coi-widget-count"
                >
                    {loading ? "…" : `${rows.length} rental${rows.length === 1 ? "" : "s"}`}
                </span>
            </CardHeader>
            <CardContent>
                {loading && (
                    <p className="text-sm text-slate-500" data-testid="coi-widget-loading">Loading…</p>
                )}
                {!loading && rows.length === 0 && (
                    <p className="text-sm text-slate-500" data-testid="coi-widget-empty">
                        No expiring COIs in the next 60 days.
                    </p>
                )}
                {!loading && rows.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                                <tr className="border-b border-slate-200">
                                    <th className="text-left py-2 pr-3">Customer</th>
                                    <th className="text-left py-2 pr-3">Rental</th>
                                    <th className="text-left py-2 pr-3">Expires</th>
                                    <th className="text-left py-2 pr-3">Days remaining</th>
                                    <th className="text-left py-2 pr-3">Refresh state</th>
                                    <th className="text-left py-2 pr-3">Days since refresh sent</th>
                                    <th className="text-right py-2 pr-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const rsLabel = REFRESH_STATE_LABEL[r.refresh_state] || r.refresh_state;
                                    const rsChip = REFRESH_STATE_CHIP[r.refresh_state] || REFRESH_STATE_CHIP.not_sent_yet;
                                    const rd = refreshDays[r.rental_id];
                                    const rdDisplay = rd == null ? "—" : `${rd}d`;
                                    return (
                                        <tr
                                            key={r.rental_id}
                                            data-testid={`coi-widget-row-${r.rental_id}`}
                                            className="border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50/60 transition-colors"
                                            onClick={() => navigate(`/admin/rentals/${r.rental_id}`)}
                                        >
                                            <td className="py-2 pr-3 font-medium text-slate-900">
                                                {r.customer_name || "—"}
                                            </td>
                                            <td className="py-2 pr-3 text-slate-700 capitalize">
                                                {(r.equipment_type || "rental").replace("_", " ")}
                                                {r.unit_number ? ` · #${r.unit_number}` : ""}
                                            </td>
                                            <td className="py-2 pr-3 text-slate-600 tabular-nums">
                                                {fmtDate(r.coi_policy_end_date)}
                                            </td>
                                            <td className="py-2 pr-3">
                                                <DaysUntilChip days={r.days_until_expiry} />
                                                {r.insurance_lapsed && (
                                                    <span
                                                        className="ml-1 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset bg-red-50 text-red-700 ring-red-200"
                                                        data-testid={`coi-widget-lapsed-${r.rental_id}`}
                                                    >
                                                        Lapsed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-2 pr-3">
                                                <span
                                                    className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset " + rsChip}
                                                    data-testid={`coi-widget-refresh-state-${r.rental_id}`}
                                                >
                                                    {rsLabel}
                                                </span>
                                            </td>
                                            <td
                                                className="py-2 pr-3 text-slate-600 tabular-nums"
                                                data-testid={`coi-widget-days-since-refresh-${r.rental_id}`}
                                            >
                                                {rdDisplay}
                                            </td>
                                            <td className="py-2 pr-2 text-right">
                                                <ArrowUpRight size={14} className="text-slate-400 inline" />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default CoiExpiringSoonCard;
